import { DragDropModule } from '@angular/cdk/drag-drop';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { TextFieldModule } from '@angular/cdk/text-field';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { MatTreeModule } from '@angular/material/tree';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { DynamicInputComponent } from '@ptg-shared/controls/dynamic-input/dynamic-input.component';
import { TooltipModule } from 'ng2-tooltip-directive';

import { CustomRichTextEditorDirective } from '../directive/custom-rich-text-editor.directive';
import { NumberOnlyModule } from '../layout/directive/number-only/number-only.module';
import { SharedModule } from '../shared.module';
import { AutocompleteComponent, CellContentSearch } from './autocomplete';
import { BannerComponent } from './banner/banner.component';
import { BreadcrumbComponent } from './breadcrumb/breadcrumb.component';
import { ButtonComponent } from './button/button.component';
import { CalendarComponent } from './calendar/calendar.component';
import { CardDescriptionComponent } from './card-description/card-description.component';
import { FieldDisplayComponent } from './card-description/field-display/field-display.component';
import { CheckboxComponent } from './checkbox/checkbox.component';
import { DatepickerMonthComponent } from './checkbox/datepicker-month/datepicker-month.component';
import { ConfirmPopupComponent } from './confirm-popup/confirm-popup.component';
import { DatepickerComponent } from './datepicker/datepicker.component';
import { DatetimePickerComponent } from './datetime-picker/datetime-picker.component';
import { DetailDisplayComponent } from './detail-display/detail-display.component';
import { DetailValueContentDirective } from './detail-display/directives/detail-value-content.directive';
import { DialogBody, DialogComponent, DialogContentHeader, DialogFooter } from './dialog';
import { DialogHeaderComponent } from './dialog-header/dialog-header.component';
import { EditorComponent } from './editor/editor.component';
import { GranulationOptions } from './granulation-option/granulation-options.component';
import {
  AddressColumnComponent,
  BinaryColumnComponent,
  CellContent,
  CellEdit,
  DatetimeColumnComponent,
  EntityReferenceColumnComponent,
  GridComponent,
  HyperlinkColumnComponent,
  IconColumnComponent,
  NumberDecimalColumnComponent,
  PersonNameColumnComponent,
  PhoneColumnComponent,
  StopPropagation,
} from './grid';
import { GridExpandableRowsComponent } from './grid-expandable-rows/grid-expandable-rows.component';
import { GridviewTransferringComponent } from './gridview_transferring/gridview_transferring.component';
import { InputAddressComponent } from './input-address/input-address.component';
import { InputAreaComponent } from './input-area/input-area.component';
import { InputColorComponent } from './input-color/input-color.component';
import { InputCurrencyComponent } from './input-currency/input-currency.component';
import { InputDatetimeComponent } from './input-datetime/input-datetime.component';
import { InputNumberMaskComponent } from './input-number-mask/input-number-mask.component';
import { InputNumberComponent } from './input-number/input-number.component';
import { InputTextComponent } from './input-text/input-text.component';
import { ListDisplayComponent } from './list-display/list-display.component';
import { MaskedEyeComponent } from './masked-eye/masked-eye.component';
import { MessageResultComponent } from './message-result/message-result.component';
import { PaginationComponent } from './pagination';
import { PanelBody, PanelComponent, PanelExtraHeader } from './panel';
import { PanelDetailDisplayBoxComponent } from './panel-detail-display-box/panel-detail-display-box.component';
import { ProgressMaskComponent } from './progress-mask/progress-mask.component';
import { ProgressSpinnerComponent } from './progress-spinner/progress-spinner.component';
import { RadioButtonComponent } from './radio-button/radio-button.component';
import { SelectComponent } from './select/select.component';
import { SpinnerComponent } from './spinner/spinner.component';
import { TextboxComponent } from './textbox/textbox.component';
import { ThreeDotsComponent } from './three-dots/three-dots.component';
import { TimepickerComponent } from './timepicker/timepicker.component';
import { TitleBarComponent } from './title-bar/title-bar.component';
import { TitleButtonComponent } from './title-button/title-button.component';
import { TitleComponent } from './title/title.component';
import { ToastComponent } from './toast/toast.component';
import { ToggleButtonComponent } from './toggle-button/toggle-button.component';
import { UploadMultipleComponent } from './upload-multiple/upload-multiple.component';
import { UploadPopupComponent } from './upload-popup/upload-popup.component';
import { UploadComponent } from './upload/upload.component';
import { ViewAddressComponent } from './view-address/view-address.component';
import { ViewRichTextDialogComponent } from './view-rich-text/view-rich-text-dialog/view-rich-text-dialog.component';
import { ViewRichTextComponent } from './view-rich-text/view-rich-text.component';
import { CalendarViewModule } from './calendar/calendar-view.module';
import { DatepickerYearComponent } from './datepicker-year/datepicker-year.component';
import { CustomTooltipComponent } from './custom-tooltip/custom-tooltip.component';
import { NumberOnlySpecModule } from '@ptg-shared/layout/directive/number/number-only-spec.module';
import { CustomTruncateTooltipComponent } from './custom-truncate-tooltip/custom-truncate-tooltip.component';
import { ChipSelectionComponent } from './chip-selection';
import { ChipItemDirective } from './chip-selection/directives';
import { AutoCompleteSearchComponent } from './auto-complete-search';
import { SelectAddChipComponent } from '@ptg-shared/transaction/components/select-add-chip/select-add-chip.component';
import { OverflowTooltipDirective } from './grid/directives/overflow-tooltip.directive';

export const COMPONENTS = [
  //#region Autocomplete declarations
  AutocompleteComponent,
  AutoCompleteSearchComponent,
  CellContentSearch,
  //#endregion

  //#region Dialog declarations
  DialogComponent,
  DialogBody,
  DialogContentHeader,
  DialogFooter,
  //#endregion

  EditorComponent,

  //#region Grid declarations
  GridComponent,
  CellContent,
  CellEdit,
  StopPropagation,
  OverflowTooltipDirective,
  DatetimeColumnComponent,
  NumberDecimalColumnComponent,
  PhoneColumnComponent,
  IconColumnComponent,
  AddressColumnComponent,
  EntityReferenceColumnComponent,
  HyperlinkColumnComponent,
  BinaryColumnComponent,
  PersonNameColumnComponent,
  //#endregion

  //#region Panel declarations
  PanelComponent,
  PanelBody,
  PanelExtraHeader,
  //#endregion

  PaginationComponent,

  ProgressMaskComponent,
  ToastComponent,
  ButtonComponent,
  ConfirmPopupComponent,
  ToggleButtonComponent,
  InputTextComponent,
  TitleBarComponent,
  TitleComponent,
  MessageResultComponent,
  InputDatetimeComponent,
  InputColorComponent,
  InputAreaComponent,
  SelectComponent,
  ThreeDotsComponent,
  GridExpandableRowsComponent,
  GridviewTransferringComponent,
  TitleButtonComponent,
  BannerComponent,
  InputNumberComponent,
  InputCurrencyComponent,
  InputNumberMaskComponent,
  CheckboxComponent,
  UploadPopupComponent,
  RadioButtonComponent,
  BreadcrumbComponent,
  CheckboxComponent,
  UploadPopupComponent,
  TextboxComponent,
  DatepickerComponent,
  TimepickerComponent,
  UploadComponent,
  DialogHeaderComponent,
  DatepickerMonthComponent,
  DatepickerYearComponent,
  ListDisplayComponent,
  MaskedEyeComponent,
  ViewRichTextDialogComponent,
  ViewRichTextComponent,
  DatetimePickerComponent,
  CustomRichTextEditorDirective,
  ProgressSpinnerComponent,
  ViewAddressComponent,
  SpinnerComponent,
  DetailDisplayComponent,
  DetailValueContentDirective,
  PanelDetailDisplayBoxComponent,
  DynamicInputComponent,
  CardDescriptionComponent,
  FieldDisplayComponent,
  GranulationOptions,
  InputAddressComponent,
  UploadMultipleComponent,
  CalendarComponent,
  CustomTooltipComponent,
  CustomTruncateTooltipComponent,

  ChipSelectionComponent,
  ChipItemDirective,
  
  SelectAddChipComponent
];

@NgModule({
  declarations: COMPONENTS,
  imports: [
    SharedModule,
    FontAwesomeModule,
    DragDropModule,
    TooltipModule,
    TextFieldModule,
    NumberOnlyModule,
    MatTreeModule,
    CalendarViewModule,
    NumberOnlySpecModule,
  ],
  exports: COMPONENTS,
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false },
    },
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ControlsModule {}
